import {
  Component, ElementRef, OnChanges, OnDestroy, SimpleChanges, inject, EventEmitter, Input, Output, ViewChild,
} from '@angular/core';
import { DatasetFormStatus } from 'src/app/@shared/form/base-form.component';
import { FormControl } from '@angular/forms';
import { FormFieldModel } from 'src/app/@shared/form/form-field/form-field.model';
import { FormFieldValue } from 'src/app/@shared/form/form.types';
import { INPUT_DEBOUNCE_TIME_MS } from 'src/app/@shared/form/form-field-config';
import { File as ProtoFile } from '@common/entities';
import { format } from 'date-fns';
import { getDisplayNames, getFileName } from 'src/app/@shared/form/form-helper';

import { Subscription } from 'rxjs';
import { CLASSIFICATION_LABELS, ClassificationKind, ClassificationService } from 'src/app/topic-classification/classification.service';

const TEXTAREA_ROWS = 3;
let nextDateTimeId = 0;

@Component({
  selector: 'proto-form-field',
  standalone: false,
  styleUrls: ['./form-field.component.scss'],
  templateUrl: './form-field.component.html',
})
export class FormFieldComponent implements OnChanges, OnDestroy {
  public optionsLoading = false;
  public optionsError = false;
  private classificationService = inject(ClassificationService);
  private optionsRequest = 0;
  private optionsChanges: Subscription = this.classificationService.changed.subscribe(({ kind }) => {
    if (kind === this.field?.id) void this.loadClassificationOptions();
  });

  public get isClassification(): boolean {
    return !!this.field && Object.prototype.hasOwnProperty.call(CLASSIFICATION_LABELS, this.field.id);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.field && this.isClassification) void this.loadClassificationOptions();
  }

  public ngOnDestroy(): void { this.optionsChanges.unsubscribe(); this.optionsRequest++; }

  public async loadClassificationOptions(): Promise<void> {
    if (!this.isClassification) return;
    const request = ++this.optionsRequest;
    this.optionsLoading = true;
    this.optionsError = false;
    try {
      const items = await this.classificationService.list(this.field.id as ClassificationKind);
      if (request !== this.optionsRequest) return;
      this.field.options = items.map(item => ({ id: item.id, label: item.title, value: item.title }));
    } catch {
      if (request === this.optionsRequest) this.optionsError = true;
    } finally {
      if (request === this.optionsRequest) this.optionsLoading = false;
    }
  }

  @ViewChild('fileInput') fileInputRef: ElementRef<HTMLInputElement>;

  @Input() public control: FormControl;
  @Input() public field: FormFieldModel;
  @Input() public status?: DatasetFormStatus;

  // Important: form is already patched when calling valueUpdated
  // using valueUpdated emitter for displaying additional fields based on field value
  @Output() public valueUpdated: EventEmitter<FormFieldValue | FormFieldValue[]> = new EventEmitter<FormFieldValue | FormFieldValue[]>();
  @Output() public openModal: EventEmitter<{ field: FormFieldModel; formGroupIndex?: number }>
    = new EventEmitter<{ field: FormFieldModel; formGroupIndex?: number }>();

  public inputDebounceTime = INPUT_DEBOUNCE_TIME_MS;
  public dateTimeId = `form-field-datetime-${nextDateTimeId += 1}`;

  public get textareaRows(): number {
    return this.isFreeTextField(this.field)
      ? 1
      : TEXTAREA_ROWS;
  }

  public onInputValueUpdated(ev: CustomEvent<HTMLIonInputElement>): void {
    this.onValueUpdated(ev.detail.value);
  }

  // FormControl type should be boolean
  public onToggleValueUpdated(ev: CustomEvent<HTMLIonToggleElement>): void {
    this.onValueUpdated(ev.detail.checked);
  }

  // we can use this for single and multiple selection as well
  public onSelectValueUpdated(ev: CustomEvent<HTMLIonSelectElement>): void {
    this.onValueUpdated(ev.detail.value);
  }

  // handle 1-n checkboxes
  // value is set for field.hasMultipleValues
  public onCheckboxValueUpdated(ev: CustomEvent<HTMLIonCheckboxElement>, value?: FormFieldValue): void {
    if (value) {
      let values: FormFieldValue[] = this.control.getRawValue() || [];

      if (!ev.detail.checked) {
        values = values.filter((v) => {
          return v !== value;
        });
      } else if (!values.includes(value)) {
        values.push(value);
      }

      this.control.patchValue(values);
      this.control.markAsDirty();

      this.onValueUpdated(values);
    } else {
      // FormControl type should be boolean
      this.onValueUpdated(ev.detail.checked);
    }
  }

  // required for field.hasMultipleValues
  public multipleCheckboxIsChecked(value: FormFieldValue): boolean {
    const values: FormFieldValue[] = this.control?.getRawValue() || [];

    if (!values?.length || !Array.isArray(values)) {
      return false;
    }

    return values.includes(value);
  }

  public onDateValueUpdated(ev: CustomEvent<HTMLIonDatetimeElement>): void {
    if (!ev.detail.value) {
      return;
    }

    // YYYY-MM-DDTHH:mm:ssZ to YYYY-MM-DD format
    const dateValue = format(ev.detail.value as string, 'yyyy-MM-dd');

    this.control.patchValue(dateValue);

    this.onValueUpdated(dateValue);
  }

  // eslint-disable-next-line class-methods-use-this
  public getDisplayName(value: any): string {
    const displayNames = getDisplayNames(value);

    if (displayNames.length === 1) {
      return displayNames[0];
    }

    return displayNames.join(', ');
  }

  // eslint-disable-next-line class-methods-use-this
  public getFileName(file: File | ProtoFile): string {
    return getFileName(file);
  }

  public onFileSelected(ev: Event): void {
    const element = ev.target as HTMLInputElement;

    if (!element.files || !element.files.length) {
      return;
    }

    this.control.patchValue(element.files[0]);
    this.control.markAsDirty();

    this.onValueUpdated(true);
  }

  public onRemoveFile(): void {
    this.control.patchValue(null);
    this.control.markAsDirty();

    // reset file input value to allow re-uploading the same file (after deleting it)
    if (this.fileInputRef) {
      this.fileInputRef.nativeElement.value = '';
    }

    this.onValueUpdated(true);
  }

  public async trimInputOnBlur(ev: FocusEvent): Promise<void> {
    const input = ev.target as HTMLIonInputElement | HTMLIonTextareaElement;
    const value = input.value ?? await input.getInputElement().then((element) => {
      return element.value;
    });

    if (typeof value !== 'string') {
      return;
    }

    const trimmedValue = value.trim();

    if (trimmedValue === value) {
      return;
    }

    input.value = trimmedValue;
    this.control.patchValue(trimmedValue);
    this.control.markAsDirty();

    this.onValueUpdated(trimmedValue);
  }

  private onValueUpdated(value: FormFieldValue | FormFieldValue[]): void {
    this.valueUpdated.emit(value);
  }

  private isFreeTextField(field: FormFieldModel): boolean {
    return `${field?.id || ''}`.endsWith('FreeText') || `${field?.parentId || ''}`.endsWith('FreeText');
  }
}
